import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Activity, Globe2, Newspaper } from 'lucide-react';
import { Card, EmptyState, Fact, Facts, ProgressBar, StatusPill } from '@/components/ui';
import { formatUsd } from '@/utils/content';
import { formatRelative } from '@/utils/format';
import { SEARCH_STATE } from '@/utils/seo';
import type { IntegrationUsage } from '@/types/api';

const PURPOSE: Record<string, string> = { ideas: 'Темы', research: 'Поиск материала', article: 'Статья' };

/** What the service is doing for the panel (ADR-0052): its sites, its spend, its domains. */
export function IntegrationUsageCard({ usage, configured }: { readonly usage: IntegrationUsage; readonly configured: boolean }): ReactNode {
  switch (usage.kind) {
    case 'google':
      return (
        <Card flush title="Сайты в Search Console" icon={Globe2}>
          {usage.sites.length === 0 ? (
            <EmptyState message={configured ? 'Сайты подключаются на вкладке SEO сайта' : 'Подключите ключ, затем сайты на вкладке SEO'} />
          ) : (
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th scope="col">Сайт</th>
                    <th scope="col">Состояние</th>
                    <th scope="col">Данные</th>
                    <th scope="col">Причина</th>
                  </tr>
                </thead>
                <tbody>
                  {usage.sites.map((site) => (
                    <tr key={site.siteId}>
                      <td>
                        <Link to={`/sites/${site.siteId}?tab=seo`}>{site.name}</Link>
                        {site.domain ? <div className="muted mono text-sm">{site.domain}</div> : null}
                      </td>
                      <td>
                        <StatusPill map={SEARCH_STATE} status={site.state} />
                      </td>
                      <td className="nowrap">{site.syncedAt ? formatRelative(site.syncedAt) : <span className="faint">—</span>}</td>
                      <td className="text-sm">{site.error ? <span className="tone-down">{site.error}</span> : <span className="faint">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      );

    case 'anthropic': {
      const share = usage.budgetUsd ? usage.monthUsd / usage.budgetUsd : 0;
      return (
        <>
          <Card title="Этот месяц" icon={Newspaper}>
            <div className="stack">
              <Facts>
                <Fact label="Расход">
                  {formatUsd(usage.monthUsd)}
                  {usage.budgetUsd ? <span className="muted"> из {formatUsd(usage.budgetUsd)}</span> : null}
                </Fact>
                <Fact label="Статей написано">{usage.articlesThisMonth}</Fact>
                <Fact label="В работе">{usage.inProgress > 0 ? <Link to="/content">{usage.inProgress}</Link> : '0'}</Fact>
                <Fact label="Не удалось">{usage.failedThisMonth > 0 ? <span className="tone-down">{usage.failedThisMonth}</span> : '0'}</Fact>
              </Facts>
              {usage.budgetUsd ? (
                <ProgressBar value={usage.monthUsd} max={usage.budgetUsd} label="Расход бюджета за месяц" tone={share >= 1 ? 'down' : share >= 0.8 ? 'degraded' : 'accent'} />
              ) : null}
              {share >= 1 ? <p className="tone-down text-sm">Бюджет исчерпан: статьи не пишутся до начала следующего месяца, публикация идёт.</p> : null}
            </div>
          </Card>
          {usage.bySite.length > 0 ? (
            <Card flush title="Расход по сайтам">
              <div className="table-scroll">
                <table>
                  <tbody>
                    {usage.bySite.map((site) => (
                      <tr key={site.siteId}>
                        <td>
                          <Link to={`/sites/${site.siteId}?tab=content`}>{site.name}</Link>
                        </td>
                        <td className="numeric">{formatUsd(site.usd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          ) : null}
          <Card flush title="Последние обращения" icon={Activity}>
            {usage.recent.length === 0 ? (
              <EmptyState message="Обращений ещё не было" />
            ) : (
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th scope="col">Когда</th>
                      <th scope="col">Сайт</th>
                      <th scope="col">Что</th>
                      <th scope="col">Модель</th>
                      <th scope="col" className="numeric">Стоимость</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usage.recent.map((call, index) => (
                      <tr key={`${call.at}-${index}`}>
                        <td className="nowrap">{formatRelative(call.at)}</td>
                        <td>{call.siteName ?? <span className="faint">—</span>}</td>
                        <td>{PURPOSE[call.purpose] ?? call.purpose}</td>
                        <td className="mono text-sm">{call.model}</td>
                        <td className="numeric">{formatUsd(call.costUsd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      );
    }

    case 'ahrefs':
    case 'dataforseo':
    case 'openpagerank': {
      const pending = usage.domains.filter((domain) => domain.pending).length;
      const failed = usage.domains.filter((domain) => domain.error).length;
      return (
        <Card flush title="Домены" icon={Globe2}>
          {usage.domains.length === 0 ? (
            <EmptyState message={configured ? 'Домены сайтов прочитаются в ближайшие минуты' : 'Домены читаются после подключения ключа'} />
          ) : (
            <>
              <div className="card-pad">
                <Facts>
                  <Fact label="Прочитано">
                    {usage.domains.length - pending} из {usage.domains.length}
                    {failed > 0 ? <span className="tone-down"> · ошибок {failed}</span> : null}
                  </Fact>
                  {usage.kind === 'ahrefs' ? <Fact label="С историей по месяцам">{usage.historyDomains}</Fact> : null}
                </Facts>
              </div>
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th scope="col">Домен</th>
                      <th scope="col">Сайт</th>
                      <th scope="col">Прочитан</th>
                      <th scope="col">Ошибка</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usage.domains.map((domain) => (
                      <tr key={domain.domain}>
                        <td className="mono">{domain.domain}</td>
                        <td>{domain.siteId ? <Link to={`/sites/${domain.siteId}?tab=seo`}>{domain.siteName}</Link> : <span className="faint">проверка перед покупкой</span>}</td>
                        <td className="nowrap">{domain.pending ? <span className="muted">в очереди</span> : domain.fetchedAt ? formatRelative(domain.fetchedAt) : '—'}</td>
                        <td className="text-sm">{domain.error ? <span className="tone-down">{domain.error}</span> : <span className="faint">—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Card>
      );
    }
  }
}
